import { useEffect, useMemo, useRef } from 'react';
import { authHintParser, DEFAULT_SCOPES, } from '../../utilities';
import { AuthInteractionStatusEnum } from '../types';
import { useAuth } from './useAuth';
/**
 * If authentication hints are present in the URL, parse them and trigger login explicitly
 * using the login api directly and return a flag to indicate that
 */
export const useAuthLoginViaHints = () => {
    const { auth, inProgress } = useAuth();
    auth.logInfo('rendering useAuthLoginViaHints');
    const isLoginFlowStarted = useRef(false);
    const willStartLoginFlowExplicitly = useRef(false);
    // Parse the authenthentication hints from the URL if present + get a clean URL
    const { loginOption, cleanUrl } = useMemo(() => {
        const { authHints, cleanUrl } = authHintParser(window.location.href) ?? {
            authHints: null,
            cleanUrl: null
        };
        // there is no authentication hints in the URL
        if (!authHints)
            return {};
        const loginOption = {
            loginHint: authHints?.username,
            tenantId: authHints?.tenantId,
            domainHint: authHints?.domain,
            scopes: DEFAULT_SCOPES,
            prompt: authHints?.prompt
        };
        // Set the flag to true if authentication hints are present, so that login flow wont be triggered by MSALAuthenticationTemplate
        auth.logInfo('useAuthLoginViaHints: There are auth hints in the url. login will be done through login api. willStartLoginFlowExplicitly to true');
        willStartLoginFlowExplicitly.current = true;
        return { loginOption, cleanUrl };
    }, []);
    // Replace the URL containing auth hints in the address bar (and browser history) with a clean one without the auth hints
    useEffect(() => {
        if (cleanUrl) {
            window.history.replaceState(window.history.state, window.document.title, cleanUrl);
        }
    }, [cleanUrl]);
    // Trigger login flow explicitly if authentication hints are present
    useEffect(() => {
        if (loginOption && inProgress === AuthInteractionStatusEnum.None && !isLoginFlowStarted.current) {
            startLoginFlow();
        }
        async function startLoginFlow() {
            try {
                isLoginFlowStarted.current = true;
                auth.logInfo('useAuthLoginViaHints: calling auth.login');
                await auth.login(loginOption);
            }
            catch (e) {
                auth.logInfo('useAuthLoginViaHints: error from calling auth.login');
                // if for some reasons we run into issue running the login api explicitly, reset this flag
                // so that the app can re-attempt to start login flow using MSALAuthenticationTemplate
                willStartLoginFlowExplicitly.current = false;
            }
        }
    }, [auth, inProgress, loginOption]);
    return willStartLoginFlowExplicitly.current;
};
//# sourceMappingURL=useAuthLoginViaHints.js.map